/**
 * SyncStatusProgressBar — thin fixed bar showing Dexie Cloud sync status.
 *
 * Ported from v1 GlobalCloudSyncStatusDexieNew.tsx (MUI → Tailwind).
 * Uses useSyncStatusBar hook for all state logic.
 *
 * Visual behavior:
 * - Indeterminate: shimmer animation (gradient slides left→right)
 * - Determinate: faint static bar at 30% opacity
 * - 2s fade-out when hiding
 * - pointer-events-none — never intercepts clicks
 *
 * Positioning (mobile / inline mode):
 * - Default: absolute at bottom of TopNavBar wrapper (below top nav)
 * - Elevated (sidebar open, task detail open, or settings open):
 *   fixed at bottom of iOS status bar (var(--status-bar-height))
 *   so it stays visible above overlays that cover the top nav.
 *
 * See: docs/plans/SYNC_STATUS_BAR_PLAN.md
 */

import { memo, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useViewContext } from "@/app/stores/viewContext"
import { useSyncStatusBar } from "./useSyncStatusBar"

interface SyncStatusProgressBarProps {
  /**
   * "fixed" — desktop: fixed at viewport top-0.
   * "inline" — mobile: context-aware positioning (see file header).
   */
  position?: "fixed" | "inline"
}

/** Matches the container's duration-[2000ms] opacity transition. */
const FADE_MS = 2000

function SyncStatusProgressBarBase({ position = "fixed" }: SyncStatusProgressBarProps) {
  const { isVisible, variant, colorVar } = useSyncStatusBar()

  // Mount the animated bars only while they can be seen (plus the fade-out).
  // They used to stay mounted at opacity 0 whenever the variant was
  // "indeterminate", so two infinite left/right keyframe animations forced
  // style, layout and paint every frame while the app sat idle — measured as
  // ~half of all main-thread time with nothing on screen moving.
  const [barsMounted, setBarsMounted] = useState(isVisible)
  useEffect(() => {
    if (isVisible) {
      setBarsMounted(true)
      return
    }
    const timer = setTimeout(() => setBarsMounted(false), FADE_MS)
    return () => clearTimeout(timer)
  }, [isVisible])

  // Track focus-banner-active on <html> to switch bar color to white
  const [focusBannerActive, setFocusBannerActive] = useState(false)
  useEffect(() => {
    const root = document.documentElement
    const observer = new MutationObserver(() => {
      setFocusBannerActive(root.classList.contains("focus-banner-active"))
    })
    setFocusBannerActive(root.classList.contains("focus-banner-active"))
    observer.observe(root, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [])

  // Context for elevated positioning (mobile only)
  const sidebarOpen = useViewContext((s) => s.sidebarOpen)
  const [searchParams] = useSearchParams()
  const isSettingsOpen = searchParams.has("setting")
  const isTaskOpen = searchParams.has("tid")

  // "Elevated" = an overlay covers the TopNavBar, so bar must sit at status bar level
  const elevated =
    position === "inline" && (sidebarOpen || isTaskOpen || isSettingsOpen)

  // When focus banner is active (inline mode), use white for visibility on colored background
  const effectiveColor = position === "inline" && focusBannerActive
    ? "white"
    : `var(${colorVar})`

  let posClass: string
  let inlineStyle: React.CSSProperties | undefined

  if (position === "fixed") {
    // Desktop: always fixed at viewport top
    posClass = "fixed top-0 left-0 right-0 z-[1000]"
  } else if (elevated) {
    // Mobile elevated: fixed at bottom of iOS status bar
    posClass = "fixed left-0 right-0 z-[1000]"
    inlineStyle = { top: "var(--status-bar-height, env(safe-area-inset-top, 0px))" }
  } else {
    // Mobile default: absolute at bottom of TopNavBar wrapper
    posClass = "absolute bottom-0 left-0 right-0 z-10"
  }

  return (
    <div
      className={`${posClass} h-[3px] pointer-events-none overflow-hidden transition-opacity duration-[2000ms] ease-in-out`}
      style={{ opacity: isVisible ? 1 : 0, ...inlineStyle }}
      role="progressbar"
      aria-hidden={!isVisible}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={variant === "determinate" ? 0 : undefined}
    >
      {variant === "indeterminate" ? (
        barsMounted && (
        <>
          {/* MUI-style dual-bar indeterminate: two staggered bars with offset timing */}
          {/* v1 pattern: gradient fades in/out for smooth shimmer effect */}
          <div
            className="absolute top-0 bottom-0 animate-indeterminate-bar1"
            style={{
              background: `linear-gradient(to right, transparent 0%, ${effectiveColor} 50%, transparent 100%)`,
              opacity: 0.4,
            }}
          />
          <div
            className="absolute top-0 bottom-0 animate-indeterminate-bar2"
            style={{
              background: `linear-gradient(to right, transparent 0%, ${effectiveColor} 50%, transparent 100%)`,
              opacity: 0.4,
            }}
          />
        </>
        )
      ) : (
        <div
          className="h-full w-full"
          style={{
            backgroundColor: effectiveColor,
            opacity: 0.3,
          }}
        />
      )}
    </div>
  )
}

export const SyncStatusProgressBar = memo(SyncStatusProgressBarBase)
